<template>
  <layout :title="title">
    <form-layout class="mx-auto">
      <form-wrapper @submit="submit()">
        <v-row class="form-row">
          <v-col cols="12" md="6">
            <input-field
              label="First Name"
              rules="required"
              v-model="form.first_name"
              name="first_name"
            ></input-field>
          </v-col>
          <v-col cols="12" md="6">
            <input-field
              label="Last Name"
              rules="required"
              v-model="form.last_name"
              name="last_name"
            ></input-field>
          </v-col>
          <v-col cols="12" md="6">
            <input-field
              label="Email"
              rules="required|email"
              v-model="form.email"
              name="email"
            ></input-field>
          </v-col>
          <v-col cols="12" md="6">
            <input-field
              label="Password"
              rules="required"
              v-model="form.password"
              :loading="isPasswordLoading"
              name="password"
            ></input-field>
          </v-col>

          <v-col cols="12" md="6">
            <phone-field
              label="Phone (M)"
              rules="phone"
              v-model="form.phone"
              name="phone"
            ></phone-field>
          </v-col>
          <v-col cols="12" md="6">
            <input-field
              label="Comission"
              placeholder="e.g. 30, 30% or 0.3"
              :rules="tiered ? '' : 'required|commission'"
              :readonly="tiered"
              :hint="tiered ? 'Set automatically from the tiers below' : ''"
              persistent-hint
              hide-details="auto"
              v-model="form.commission_rate"
            ></input-field>
            <v-checkbox
              class="mt-2 pt-0"
              v-model="tiered"
              hide-details
              dense
            >
              <template v-slot:label>
                Tiered commission
                <v-tooltip bottom max-width="320">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-1" v-bind="attrs" v-on="on"
                      >mdi-help-circle-outline</v-icon
                    >
                  </template>
                  <span
                    >The commission goes up automatically as the affiliate's
                    clients bring more. Commissions already earned keep their
                    rate.</span
                  >
                </v-tooltip>
              </template>
            </v-checkbox>
          </v-col>
          <v-col cols="12" v-if="tiered">
            <v-card outlined class="tiers-card pa-5">
              <div class="subtitle-1 font-weight-medium">Commission tiers</div>
              <div class="body-small text--secondary mb-4">
                The rate goes up once the affiliate's clients have brought the
                amount in "From". Commissions already earned keep their rate.
              </div>
              <v-row dense>
                <v-col cols="12" sm="5">
                  <date-field
                    label="Tiers start"
                    rules="required"
                    hide-details="auto"
                    v-model="form.commission_tiers_start"
                  ></date-field>
                  <div class="body-small text--secondary mt-1">
                    Payments from this date count towards the tiers.
                  </div>
                </v-col>
              </v-row>
              <v-row dense class="mt-4 tiers-head body-small">
                <v-col cols="4">From</v-col>
                <v-col cols="4">Commission</v-col>
                <v-col cols="3" class="d-none d-md-block">Range</v-col>
              </v-row>
              <v-row
                v-for="(tier, index) in tierRows"
                :key="index"
                dense
                align="center"
                class="tier-row"
              >
                <v-col cols="4">
                  <input-field
                    rules="required"
                    type="number"
                    prefix="$"
                    hide-details="auto"
                    :readonly="index == 0"
                    v-model="tier.from"
                  ></input-field>
                </v-col>
                <v-col cols="4">
                  <input-field
                    placeholder="e.g. 30, 30% or 0.3"
                    rules="required|commission"
                    hide-details="auto"
                    v-model="tier.rate"
                  ></input-field>
                </v-col>
                <v-col cols="3" class="d-none d-md-block">
                  <span class="body-small text--secondary text-no-wrap">
                    {{ tierRange(index) }}
                  </span>
                </v-col>
                <v-col cols="4" md="1" class="text-right">
                  <v-btn
                    icon
                    small
                    v-if="index != 0"
                    title="Remove tier"
                    @click="tierRows.splice(index, 1)"
                    ><v-icon small>mdi-delete-outline</v-icon></v-btn
                  >
                </v-col>
              </v-row>
              <v-btn
                text
                small
                color="primary"
                class="px-1 mt-2"
                @click="addTier()"
                ><v-icon left small>mdi-plus</v-icon>Add tier</v-btn
              >
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-checkbox
              class="mt-0"
              v-model="form.affiliate_access"
              label="Enable Affiliate Access"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-checkbox
              class="mt-0"
              v-model="form.notify_by_email"
              label="Notify Affiliate by Email"
            />
          </v-col>
        </v-row>
        <div class="d-flex">
          <v-spacer></v-spacer>
          <close-button
            :disabled="loading"
            :to="{ name: 'affiliates' }"
          ></close-button>
          <secondary-button
            class="ml-3"
            :loading="loading"
            :disabled="isPasswordLoading"
            type="submit"
            >Submit</secondary-button
          >
        </div>
      </form-wrapper>
    </form-layout>
  </layout>
</template>

<script>
import Layout from "@/components/ui/Layout.vue";
import InputField from "@/components/ui/form/InputField.vue";
import DateField from "@/components/ui/form/DateField.vue";
import PhoneField from "@/components/ui/form/PhoneField.vue";
import CloseButton from "@/components/ui/buttons/CloseButton.vue";
import SecondaryButton from "@/components/ui/buttons/SecondaryButton.vue";
import FormWrapper from "@/components/ui/form/FormWrapper.vue";
import {initForm} from "@/assets/utils/common.utils";
import {colAffiliates, functions} from "@/assets/utils/firebase.utils";
import {doc, getDoc, getDocs, limit, query, where} from "firebase/firestore";
import {httpsCallable} from "firebase/functions";
import FormLayout from "@/components/ui/FormLayout.vue";

export default {
  components: {
    Layout,
    InputField,
    DateField,
    PhoneField,
    CloseButton,
    SecondaryButton,
    FormWrapper,
    FormLayout,
  },
  data() {
    return {
      loading: false,
      isLoading: false,
      isPasswordLoading: false,
      passwordLoaded: false,
      tiered: false,
      tierRows: [{ from: "0", rate: "" }],
      form: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        commission_rate: "0.00",
        password: "",
        affiliate_access: false,
        notify_by_email: false,
        commission_tiers: [],
        commission_tiers_start: "",
      },
    };
  },
  computed: {
    isUpdate() {
      return this.$route.name == "update-affiliate";
    },
    title() {
      return this.isUpdate ? "Edit Affiliate" : "Add New Affiliate";
    },
  },
  methods: {
    async submit() {
      const vm = this;
      if (vm.isUpdate && !vm.passwordLoaded) {
        vm.notifyError(
          "The affiliate's password could not be loaded, so saving would overwrite it. Reload the page and try again."
        );
        return;
      }
      try {
        vm.loading = true;
        let affiliateId = this.$route.params.id;
        let email = vm.form.email.trim().toLowerCase();
        let formDoc = {
          ...vm.form,
          email,
          commission_rate: vm.normalizeRate(vm.form.commission_rate),
          commission_tiers: [],
          commission_tiers_start: null,
        };
        if (vm.tiered) {
          // The server sets commission_rate from the volume for tiered affiliates.
          formDoc.commission_tiers = vm.tierRows.map((i) => ({
            from: parseFloat(i.from),
            rate: vm.normalizeRate(i.rate),
          }));
          formDoc.commission_tiers_start = vm.form.commission_tiers_start;
        }
        let q = query(colAffiliates, where("email", "==", email), limit(1));
        let snap = (await getDocs(q)).docs;
        if (snap.length != 0 && affiliateId != snap[0].id) {
          vm.loading = false;
          vm.notifyError(
            "Another affiliate already exists with the same Email, please enter a unique Email."
          );
          return;
        }
        let request = {};
        if (vm.isUpdate) {
          formDoc.id = affiliateId;
          request = {
            action: "update",
            data: formDoc,
          };
        } else {
          formDoc.commission = 0;
          formDoc.commission_owed = 0;
          request = {
            action: "create",
            data: formDoc,
          };
        }
        await httpsCallable(functions, "manageAffiliate")(request);
        if (vm.isUpdate) {
          vm.notifySuccess("Affiliate updated successfully.");
        } else {
          vm.notifySuccess("Affiliate added successfully.");
        }
        vm.$router.replace({ name: "affiliates" });
        vm.loading = false;
      } catch (error) {
        vm.handleError(error);
      }
    },
    // "30%" or "30" -> 0.3, "1%" or "1" -> 0.01, "0.3" -> 0.3 (already decimal)
    normalizeRate(value) {
      let rateStr = String(value).trim();
      let hasPercent = rateStr.endsWith("%");
      let rateNum = parseFloat(rateStr.replace("%", ""));
      return hasPercent || rateNum >= 1 ? rateNum / 100 : rateNum;
    },
    tierRange(index) {
      let format = (v) =>
        "$" + Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 });
      let from = parseFloat(this.tierRows[index].from);
      let next = this.tierRows[index + 1];
      if (isNaN(from)) return "";
      if (!next || isNaN(parseFloat(next.from))) return `${format(from)} and up`;
      return `up to ${format(parseFloat(next.from) - 0.01)}`;
    },
    addTier() {
      let last = this.tierRows[this.tierRows.length - 1];
      let from = (parseFloat(last.from) || 0) + 100000;
      this.tierRows.push({ from: String(from), rate: "" });
    },
    async init() {
      const vm = this;
      if (!vm.isUpdate){
        this.form.password = Math.random().toString(36).slice(-8);
        return;
      }
      try {
        vm.isLoading = true;
        let affiliateId = this.$route.params.id;
        let affiliate = (await getDoc(doc(colAffiliates, affiliateId))).data();
        initForm(vm.form, affiliate);
        // The document holds the encrypted password; the real one comes from
        // fetchPassword. Never let the ciphertext reach the field or the save.
        vm.form.password = "";
        vm.tiered = (affiliate.commission_tiers || []).length > 0;
        if (vm.tiered) {
          vm.tierRows = affiliate.commission_tiers.map((i) => ({
            from: String(i.from),
            rate: String(parseFloat((i.rate * 100).toFixed(2))),
          }));
        }
        vm.fetchPassword();
        vm.isLoading = false;
      } catch (error) {
        vm.handleError(error);
      }
    },
    async fetchPassword() {
      try {
        this.isPasswordLoading = true;
        let request = {
          action: "password",
          data: { id: this.$route.params.id },
        };
        let getPassword = httpsCallable(functions, "manageAffiliate");
        let password = (await getPassword(request)).data;
        this.form.password = password;
        this.passwordLoaded = true;
        this.isPasswordLoading = false;
      } catch (error) {
        this.isPasswordLoading = false;
        this.handleError(error);
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped>
.tiers-card {
  border-radius: 8px;
}
.tiers-head {
  color: rgba(0, 0, 0, 0.6);
}
.tier-row + .tier-row {
  margin-top: 4px;
}
</style>
